import { cn } from '@utils/cn'

interface ExampleAccordionProps {
  readonly content: string
  readonly messageKey?: string // Deprecated: kept for backward compatibility, not used
  readonly className?: string
}

/**
 * Componente que detecta e integra exemplos nas mensagens do chat de forma natural.
 * Detecta padrões como "Exemplo:", "ex:", "exemplo:" seguidos de texto e os mostra inline.
 */
export function ExampleAccordion({ content, className }: Readonly<ExampleAccordionProps>) {
  // Detectar padrões de exemplo: "Exemplo:", "ex:", "exemplo:" seguido de texto
  // Padrão seguro contra ReDoS: usa quantificador não-guloso e limita backtracking
  // Limita a captura a no máximo 1000 caracteres para prevenir DoS
  const examplePattern = /(?:Exemplo|exemplo|ex|Ex)[:\s]+([^.\n]{0,1000}?)(?:\.|$|\n)/gi
  const matches = Array.from(content.matchAll(examplePattern))

  // Se não há exemplos, retornar conteúdo normal
  if (matches.length === 0) {
    return <p className={cn('text-sm whitespace-pre-wrap', className)}>{content}</p>
  }

  // Separar conteúdo em partes: antes do exemplo, exemplo, depois do exemplo
  const parts: Array<{ type: 'text' | 'example'; content: string; index: number }> = []
  let lastIndex = 0

  matches.forEach((match, matchIndex) => {
    if (match.index !== undefined) {
      // Adicionar texto antes do exemplo
      if (match.index > lastIndex) {
        const textBefore = content.substring(lastIndex, match.index).trim()
        if (textBefore) {
          parts.push({ type: 'text', content: textBefore, index: matchIndex * 2 })
        }
      }
      // Adicionar exemplo
      const exampleText = match[0].trim()
      parts.push({ type: 'example', content: exampleText, index: matchIndex * 2 + 1 })
      lastIndex = match.index + match[0].length
    }
  })

  // Adicionar texto restante após o último exemplo
  if (lastIndex < content.length) {
    const textAfter = content.substring(lastIndex).trim()
    if (textAfter) {
      parts.push({ type: 'text', content: textAfter, index: parts.length })
    }
  }

  return (
    <div className={cn('text-sm whitespace-pre-wrap', className)}>
      {parts.map((part) => {
        if (part.type === 'text') {
          return <span key={part.index}>{part.content} </span>
        } else {
          // Extrair apenas o texto do exemplo (sem "Exemplo:" ou "ex:")
          const exampleText = part.content.replace(/^(?:Exemplo|exemplo|ex|Ex)[:\s]+/i, '').trim()
          
          // Mostrar exemplo inline, integrado na mensagem, em itálico e com cor mais suave
          return (
            <span key={part.index} className="text-gray-600 italic">
              {exampleText}
            </span>
          )
        }
      })}
    </div>
  )
}
